import React from 'react'
import { submissionService } from '@oneblink/apps-react'
import { FormTypes } from '@oneblink/types'

import AutoSaveControlledForm from '../../../components/AutoSaveControlledForm'

import { useAppConfig } from '../../../providers/AppConfigProvider'
import useSubmitForm from '../../../hooks/useSubmitForm'
import {
  generateOrderSummaryTable,
  setOrderSummaryTableInlineStyles,
  OrderSummaryItem,
} from '../../../lib/generateOrderSummaryTable'
import {
  findElement,
  findSubForm,
  replaceElement,
} from '../../../lib/definition-manipulation'

type Props = { form: FormTypes.Form; preFillData: Record<string, unknown> }

interface Resource {
  Quantity?: string
  Languages?: Array<{ Select_a_language: string; Quantity: string }>
}

interface ResourceOrderSubFormSubmission extends Resource {
  englishBrochureId__hidden: string
}

interface ResourceOrderElement extends Resource {
  name: string
}

function buildLiteratureList({
  definition,
  submission,
}: submissionService.NewFormSubmission) {
  const literatureMap = new Map()
  const orderedLiterature: ResourceOrderSubFormSubmission[] = []
  for (const topLevelElement of definition.elements) {
    if (
      topLevelElement.type === 'form' &&
      topLevelElement.elements &&
      topLevelElement.elements.length > 0
    ) {
      // get english language brochure Id
      const englishLiteratureIdElement = topLevelElement.elements.find(
        (el) => el.type === 'text' && el.name === 'englishBrochureId__hidden',
      ) as FormTypes.TextElement | undefined
      if (!englishLiteratureIdElement) {
        continue
      }
      if (submission[topLevelElement.name]) {
        orderedLiterature.push(
          submission[topLevelElement.name] as ResourceOrderSubFormSubmission,
        )
      }
      const englishLiteratureId = englishLiteratureIdElement.defaultValue
      literatureMap.set(englishLiteratureId, '0')
      //get other languages
      for (const subElement of topLevelElement.elements) {
        if (
          subElement.type === 'repeatableSet' &&
          subElement.name === 'Languages'
        ) {
          const langaugeRepetableSet = subElement
          for (const subRepSetElement of langaugeRepetableSet.elements) {
            if (
              subRepSetElement.type === 'select' &&
              subRepSetElement.name === 'Select_a_language'
            ) {
              const langaugeSelectOptions = subRepSetElement.options
              for (const lang of langaugeSelectOptions || []) {
                literatureMap.set(lang.value, '0')
              }
            }
          }
        }
      }
    }
  }
  for (const order of orderedLiterature) {
    if (order.Quantity) {
      //set english language quantity
      literatureMap.set(order.englishBrochureId__hidden, order.Quantity)
    }
    if (order.Languages && order.Languages.length > 0) {
      order.Languages.forEach((lang) =>
        literatureMap.set(lang.Select_a_language, lang.Quantity),
      )
    }
  }
  return Array.from(literatureMap, ([key, value]) => ({
    literatureId: key,
    quantity: value,
  })).filter(({ quantity }) => quantity !== '0')
}

function Form({ form, preFillData }: Props) {
  const { buttons } = useAppConfig()
  const [previousIAmValue, setPreviousIAmValue] = React.useState(
    preFillData.iAm,
  )

  /* update definition here */
  const updateDefinition = React.useCallback(
    (submission: Record<string, unknown>, definition: FormTypes.Form) => {
      if (submission) {
        // Build document order summary
        const documentQtys: ResourceOrderElement[] = Object.keys(submission)
          .filter((key) => /^literature_/i.test(key))
          .map((key) => ({ name: key, ...Object(submission[key]) }))
          .filter(
            (obj: ResourceOrderElement) =>
              !!obj.Quantity || !!obj.Languages?.length,
          )

        const documentElementNames = documentQtys.map((d) => d.name)
        const documentDefs = documentElementNames
          .map((subFormName) => findSubForm(definition, subFormName)?.value)
          .filter(Boolean)

        const summaryItems = documentDefs.reduce((memo, def) => {
          if (def) {
            const qty = documentQtys.find((q) => q.name === def.name)
            if (qty) {
              const languageMap = findElement<FormTypes.CheckboxElement>(
                def,
                'Select_a_language',
              )?.value?.options?.reduce(
                (memo, val) => memo.set(val.value, val.label),
                new Map(),
              )

              const languageQty: Array<{ lang: string; quantity: string }> = []
              if (qty.Quantity) {
                languageQty.push({ lang: 'English', quantity: qty.Quantity })
              }

              if (qty.Languages && qty.Languages.length > 0) {
                qty.Languages.forEach((item) => {
                  if (item.Quantity && languageMap) {
                    const lang = languageMap.get(item.Select_a_language)
                    languageQty.push({ lang, quantity: item.Quantity })
                  }
                })
              }

              const titleEl = findElement<FormTypes.HeadingElement>(
                def,
                'literatureTitle',
              )

              memo = memo.concat(
                languageQty.map((langQty) => ({
                  label: titleEl?.value.label || '',
                  quantity: parseInt(langQty.quantity),
                  language: langQty.lang,
                })),
              )
            }
          }
          return memo
        }, [] as Array<OrderSummaryItem>)

        const existingSummary = findElement<FormTypes.HtmlElement>(
          definition,
          'orderSummaryTable',
        )

        const orderSummaryForm = generateOrderSummaryTable(summaryItems)

        if (existingSummary && existingSummary.parent.elements) {
          existingSummary.parent.elements = replaceElement(
            existingSummary.parent.elements,
            existingSummary.value,
            orderSummaryForm,
          )
        }
      }

      return definition
    },
    [],
  )

  /* update submission data here */
  const updateSubmission = React.useCallback(
    (newSubmission: Record<string, unknown>) => {
      let resetSubmissionDetails = newSubmission
      if (previousIAmValue !== newSubmission.iAm) {
        resetSubmissionDetails = Object.keys(newSubmission).reduce(
          (memo, key) => {
            if (/^literature_/i.test(key)) {
              const id = (memo[key] as ResourceOrderSubFormSubmission)
                .englishBrochureId__hidden
              memo[key] = { englishBrochureId__hidden: id }
            }

            return memo
          },
          { ...newSubmission },
        )
      }

      setPreviousIAmValue(newSubmission.iAm)
      return resetSubmissionDetails
    },
    [previousIAmValue],
  )

  const [
    { onSubmit, clearServerValidationMessage },
    { isSubmitting, serverValidationMessage },
  ] = useSubmitForm({
    formDefinition: form,
    preFillData,
    skipServerValidation: false,
  })

  const handleSubmit = React.useCallback(
    (newFormSubmission: submissionService.NewFormSubmission) => {
      newFormSubmission.submission.literatureList =
        buildLiteratureList(newFormSubmission)
      setOrderSummaryTableInlineStyles(newFormSubmission.definition)
      onSubmit(newFormSubmission)
    },
    [onSubmit],
  )

  return (
    <AutoSaveControlledForm
      form={form}
      initialSubmission={preFillData}
      onSubmit={handleSubmit}
      buttons={buttons}
      updateSubmission={updateSubmission}
      updateDefinition={updateDefinition}
      isSubmitting={isSubmitting}
      serverValidationMessage={serverValidationMessage}
      clearServerValidationMessage={clearServerValidationMessage}
    />
  )
}

export default Form
